'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import QuantityStepper from '@/components/cart/QuantityStepper';
import { useCart, type CartLine } from '@/context/CartContext';
import { useI18n } from '@/i18n/LocaleProvider';
import { formatDate, formatPrice } from '@/i18n/config';

export default function CartLineItem({ line, onNavigate }: { line: CartLine; onNavigate?: () => void }) {
  const { setQuantity, removeItem } = useCart();
  const { locale, t } = useI18n();
  const { product, quantity } = line;

  return (
    <li className="flex gap-4 py-4">
      <Link
        href={`/product?slug=${product.slug}`}
        onClick={onNavigate}
        className="relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-muted/30 border border-border"
      >
        <AppImage src={product.image} alt={product.imageAlt} fill sizes="80px" className="object-contain p-1.5" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          href={`/product?slug=${product.slug}`}
          onClick={onNavigate}
          className="block text-sm font-bold text-foreground leading-snug line-clamp-2 hover:text-primary transition-colors"
        >
          {product.name}
        </Link>
        {product.preorder && (
          <p className="mt-0.5 text-xs font-semibold text-accent">
            {t.shop.preorder} · {t.shop.releases(formatDate(product.preorder.releaseDate, locale))}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <QuantityStepper value={quantity} onChange={(q) => setQuantity(product.id, q)} />
          <span className="text-sm font-bold text-foreground tabular-nums">{formatPrice(product.price * quantity, locale)}</span>
        </div>
        <button
          type="button"
          onClick={() => removeItem(product.id)}
          className="mt-1 text-xs text-muted-foreground hover:text-red-500 transition-colors"
        >
          {t.cart.remove}
        </button>
      </div>
    </li>
  );
}
